import React, { useMemo, useState } from 'react';
import { toast } from 'sonner';
import type { AdminRole } from '../../types/config';
import type { AdminPage, AdminPageProps } from '../../types/navigation';
import { useStore } from '../../contexts/StoreContext';
import { ADMIN_USERS } from '../../data/users';
import { AdminSidebar } from '../../components/admin/AdminSidebar';
import { AdminTopbar } from '../../components/admin/AdminTopbar';
import { pagesFor } from '../../utils/permissions';
import { needsVerification, readyForNote } from '../../utils/caseStatus';
import { AdminLogin } from './AdminLogin';
import { Dashboard } from './Dashboard';
import { CaseList } from './CaseList';
import { CaseDetail } from './CaseDetail';
import { Inventory } from './Inventory';
import { Stations } from './Stations';
import { WorkQueue } from './WorkQueue';
const queue = (page: AdminPage) => (p: AdminPageProps) => <WorkQueue {...p} page={page} data-id="element-407" data-mp="3shhaj2x4j01b" />;
const PAGES: Record<AdminPage, React.ComponentType<AdminPageProps>> = {
  dashboard: Dashboard,
  cases: CaseList,
  inventory: Inventory,
  stations: Stations,
  verification: queue('verification'),
  payments: queue('payments'),
  notesheet: queue('notesheet'),
  approval: queue('approval'),
  letters: queue('letters'),
  handover: queue('handover'),
  reports: queue('reports'),
  users: queue('users'),
  config: queue('config'),
  audit: queue('audit')
};
export function AdminApp({
  onExit
}: {
  onExit: () => void;
}) {
  const {
    cases
  } = useStore();
  const [role, setRole] = useState<AdminRole | null>(null);
  const [page, setPage] = useState<AdminPage>('dashboard');
  const [focus, setFocus] = useState<string | undefined>();
  const [detail, setDetail] = useState<string | null>(null);
  const counts = useMemo(() => ({
    verification: cases.filter(needsVerification).length,
    payments: cases.filter(c => c.paymentStatus === 'paid' || c.paymentStatus === 'failed').length,
    notesheet: cases.filter(readyForNote).length,
    approval: cases.filter(c => c.approval === 'pending').length,
    letters: cases.filter(c => c.approval === 'approved' && (!c.letter || c.letter.status === 'draft' || c.letter.status === 'signed')).length
  }), [cases]);
  if (!role) return <AdminLogin onLogin={r => {
    setRole(r);
    setPage(defaultPage(r));
  }} onExit={onExit} data-id="element-408" data-mp="11plzn7f3sl0h" />;
  const allowed = pagesFor(role);
  const go = (p: AdminPage, pcr?: string) => {
    if (!allowed.includes(p)) {
      toast.error('এই মডিউল আপনার ভূমিকায় অনুমোদিত নয়।');
      return;
    }
    setPage(p);
    setFocus(pcr);
    setDetail(null);
  };
  const changeRole = (r: AdminRole) => {
    setRole(r);
    if (!pagesFor(r).includes(page)) setPage(defaultPage(r));
    toast.info(`ভূমিকা পরিবর্তিত: ${ADMIN_USERS[r].title}`);
  };
  const Current = PAGES[allowed.includes(page) ? page : 'dashboard'];
  const props: AdminPageProps = {
    role,
    go,
    openCase: pcr => setDetail(pcr),
    focus
  };
  return <div className="flex h-full w-full bg-canvas" data-id="element-409" data-mp="28ekc6752qqzq">
      <AdminSidebar active={detail ? 'cases' : page} allowed={allowed} onNavigate={p => go(p)} counts={counts} data-id="element-410" data-mp="28d0ymoasyu1n" />
      <div className="flex min-w-0 flex-1 flex-col" data-id="element-411" data-mp="2gp17yndqwrja">
        <AdminTopbar role={role} onRole={changeRole} onLogout={() => setRole(null)} onExit={onExit} onOpenCase={p => setDetail(p)} allowed={allowed} onNavigate={p => go(p)} data-id="element-412" data-mp="2ko1p81gkrm46" />
        <main className="flex-1 overflow-y-auto scroll-thin px-4 py-5 lg:px-8 lg:py-6" key={`${detail ?? page}-${role}`} data-id="element-413" data-mp="2umpv61vacari">
          {detail ? <CaseDetail {...props} pcr={detail} onBack={() => setDetail(null)} data-id="element-414" data-mp="2zl16kh18atbu" /> : <Current {...props} data-id="element-415" data-mp="33nfjylm4p0vq" />}
        </main>
      </div>
    </div>;
}
function defaultPage(r: AdminRole): AdminPage {
  const map: Partial<Record<AdminRole, AdminPage>> = {
    verifier: 'verification',
    dealing: 'notesheet',
    approver: 'approval',
    signatory: 'letters',
    accounts: 'payments',
    station: 'inventory'
  };
  return map[r] ?? 'dashboard';
}